'use client';

import React, { useCallback, useMemo } from 'react';
import { cn } from '@/lib/cn';
import { AppBar, Button } from '@/components/ui';
import { useStore } from '@/providers/StoreProvider';
import { NeighborhoodMap } from './NeighborhoodMap';
import { NeighborhoodAccordion } from './NeighborhoodAccordion';
import { SelectedNeighborhoodChips } from './SelectedNeighborhoodChips';
import type { ExtendedNeighborhood } from './NeighborhoodGroupedList';
import { useAnalytics } from '@/hooks/useAnalytics';

export interface NeighborhoodMapPopoverProps {
  className?: string;
  onClose?: () => void;
  selectionMode?: 'store' | 'external';
  selectedNeighborhoodIds?: string[];
  onToggleNeighborhood?: (id: string) => void;
  onToggleNeighborhoodWithChildren?: (id: string) => void;
  onToggleBorough?: (borough: string) => void;
  onResetExternalSelection?: () => void;
  onApplyExternalSelection?: (ids: string[]) => void | Promise<void>;
}

export function NeighborhoodMapPopover({
  className,
  onClose,
  selectionMode = 'store',
  selectedNeighborhoodIds,
  onToggleNeighborhood,
  onToggleNeighborhoodWithChildren,
  onToggleBorough,
  onResetExternalSelection,
  onApplyExternalSelection,
}: NeighborhoodMapPopoverProps) {
  const analytics = useAnalytics();
  const isExternal = selectionMode === 'external';
  const neighborhoods = useStore(s => s.neighborhoods.neighborhoods) as ExtendedNeighborhood[];
  const storeNeighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const storeToggleNeighborhood = useStore(s => s.listingsFilters.toggleNeighborhood);
  const storeToggleNeighborhoodWithChildren = useStore(
    s => s.listingsFilters.toggleNeighborhoodWithChildren,
  );
  const storeToggleBorough = useStore(s => s.listingsFilters.toggleBorough);
  const resetNeighborhoods = useStore(s => s.listingsFilters.resetNeighborhoods);

  // Selections apply as they are made — on the map, in the accordion and on the
  // chips alike — so the map is just another view onto the same selection the
  // rest of the location filter edits. "Done" only dismisses the popover.
  const noop = useCallback(() => undefined, []);
  const activeNeighborhoodIds = useMemo(
    () => (isExternal ? selectedNeighborhoodIds ?? [] : storeNeighborhoodIds ?? []),
    [isExternal, selectedNeighborhoodIds, storeNeighborhoodIds],
  );
  const toggleNeighborhood = isExternal ? onToggleNeighborhood ?? noop : storeToggleNeighborhood;
  const toggleNeighborhoodWithChildren = isExternal
    ? onToggleNeighborhoodWithChildren ?? noop
    : storeToggleNeighborhoodWithChildren;
  const toggleBorough = isExternal ? onToggleBorough ?? noop : storeToggleBorough;

  // Both toggles fall back to a plain single toggle for an id that isn't a
  // parent, so the map doesn't need to look up children itself.
  const handleNeighborhoodClick = useCallback(
    (neighborhoodId: string) => {
      toggleNeighborhoodWithChildren(neighborhoodId);
    },
    [toggleNeighborhoodWithChildren],
  );

  const handleReset = useCallback(() => {
    analytics.track('listings_filter_allneighborhoods_map_resetselections');
    if (isExternal) {
      onResetExternalSelection?.();
      return;
    }
    resetNeighborhoods();
  }, [analytics, isExternal, onResetExternalSelection, resetNeighborhoods]);

  const handleDone = useCallback(() => {
    analytics.track('listings_filter_allneighborhoods_map_seeresults');
    void (async () => {
      // External hosts persist their draft on apply (e.g. saving a listing
      // alert), which store mode has no equivalent of.
      if (isExternal) {
        await onApplyExternalSelection?.(activeNeighborhoodIds);
      }
      onClose?.();
    })();
  }, [activeNeighborhoodIds, analytics, isExternal, onApplyExternalSelection, onClose]);

  return (
    <div
      className={cn(
        'flex flex-col',
        'bg-primary border border-tertiary overflow-hidden',
        'rounded-4xl',
        'shadow',
        'gap-4 pb-2',
        'max-w-[800px]',
        className,
      )}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      <AppBar
        title="All neighborhoods"
        onBack={() => {
          analytics.track('listings_filter_allneighborhoods_map_goback');
          onClose?.();
        }}
      />
      {activeNeighborhoodIds.length > 0 ? (
        <div className="px-6">
          <SelectedNeighborhoodChips
            mobile={true}
            selectedNeighborhoodIds={activeNeighborhoodIds}
            onToggleNeighborhood={toggleNeighborhood}
            onToggleNeighborhoodWithChildren={toggleNeighborhoodWithChildren}
            onToggleBorough={toggleBorough}
            includeBuilding={!isExternal}
            className="-mx-6 px-6"
          />
        </div>
      ) : null}

      <div className="flex h-[350px] items-start gap-4 overflow-hidden pr-6">
        <div className="w-[320px] h-full min-h-0 overflow-y-auto px-4">
          <NeighborhoodAccordion
            className="w-full"
            selectedNeighborhoodIds={activeNeighborhoodIds}
            onToggleNeighborhood={toggleNeighborhood}
            onToggleNeighborhoodWithChildren={toggleNeighborhoodWithChildren}
            onToggleBorough={toggleBorough}
            resetBuildingOnSelect={!isExternal}
          />
        </div>

        <div className="flex-1 rounded-2xl overflow-hidden">
          <NeighborhoodMap
            neighborhoods={neighborhoods}
            selectedNeighborhoodIds={activeNeighborhoodIds}
            onNeighborhoodClick={handleNeighborhoodClick}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 px-6 py-4 border-t border-secondary">
        <Button
          variant="tertiary"
          size="sm"
          onClick={e => {
            e.stopPropagation();
            handleReset();
          }}
          disabled={activeNeighborhoodIds.length === 0}
          className="w-32"
        >
          Reset
        </Button>
        <Button
          variant="primary"
          size="sm"
          className="w-32"
          onClick={e => {
            e.stopPropagation();
            handleDone();
          }}
        >
          Done
        </Button>
      </div>
    </div>
  );
}

export default NeighborhoodMapPopover;
